import { FC, useContext, useState } from 'react';
import { Button, Stack, Typography } from '@mui/material';
import {
  useUpdateAssociate,
  UpdateAssociatePayload,
  AuthenticationContext,
} from '@vs-online-only-app/state';
import { ProfileSetupContext } from '../../ProfileSetupContext';
import ProfileSetupError from '../../ProfileSetupError';
import ProfilePictureInfo from './ProfilePictureInfo';
import UploadProfilePicture from './UploadProfilePicture';
import ProfileSetupContinueButton from '../../ProfileSetupContinueButton';
import ProfileSetupBackButton from '../../ProfileSetupBackButton';

export type ProfilePicture = UpdateAssociatePayload['picture'];

const SetProfilePicture: FC = () => {
  const { user } = useContext(AuthenticationContext);

  const [error, setError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);
  const [picture, setPicture] = useState<ProfilePicture>();

  const { completeProfileSetup } = useContext(ProfileSetupContext);
  const updateAssociate = useUpdateAssociate();

  const handleSubmit = async () => {
    setError(false);
    setIsLoading(true);

    try {
      if (!picture?.base64) {
        throw new Error('No picture provided');
      }

      await updateAssociate.mutateAsync({
        picture,
      });

      await completeProfileSetup();
    } catch (e) {
      console.error(e);
      setError(true);
    }

    setIsLoading(false);
  };

  const handleSkip = async () => {
    setError(false);
    setIsLoading(true);

    try {
      await completeProfileSetup();
    } catch (e) {
      console.error(e);
      setError(true);
    }

    setIsLoading(false);
  };

  const handleProfilePictureChange = (picture: ProfilePicture) => {
    setError(false);
    setPicture(picture);
  };

  return (
    <Stack gap={4} sx={{ position: 'relative' }}>
      <ProfileSetupBackButton />
      {picture ? (
        <Typography variant="h5" textAlign="center">
          Looking good, {user?.firstName}.
        </Typography>
      ) : (
        <Stack gap={1} alignItems="center">
          <Typography variant="h5" textAlign="center">
            Upload a profile picture
          </Typography>
          <Typography>Your customers will see this.</Typography>
        </Stack>
      )}
      {error && <ProfileSetupError title="profile picture" />}
      <Stack gap={4}>
        <UploadProfilePicture
          imgSrc={picture?.base64}
          setProfilePicture={handleProfilePictureChange}
        />
        {!picture && <ProfilePictureInfo />}
        <Stack gap={1}>
          <ProfileSetupContinueButton
            onClick={handleSubmit}
            loading={isLoading}
            disabled={!picture}
          >
            Finish
          </ProfileSetupContinueButton>
          <Button variant="text" size="small" onClick={handleSkip}>
            <Typography variant="body2" component="span" fontWeight="inherit">
              Skip for now
            </Typography>
          </Button>
        </Stack>
      </Stack>
    </Stack>
  );
};

export default SetProfilePicture;
